import * as React from "react";
import {ListBase, ListProps, Record, useDataProvider, useListContext, useNotify, usePermissions, useQuery} from "react-admin";
import {Button, Card, Switch, Table, Select} from "antd";
import {ExtendedDataProvider} from "@src/types";
import {Link} from "react-router-dom";
import SkeletonTable, {SkeletonTableColumnsType} from "../Common/SkeletonTable";
import {LiveTableWrapper} from "@src/components/Common/tableUtils";
import {BaseFilterDropdown} from "@src/components/Filters/BaseFilterDropdown";
import PackDashboardTabs from "../Pack/PackDashboard/PackDashboardTabs";

const { Option } = Select;

const AIProcessorListView = () => {
    const { ids, data, loading, total, perPage, setPage, page, refetch, setFilters, filterValues } = useListContext();
    const dataProvider: ExtendedDataProvider = useDataProvider();
    const notify = useNotify();
    const { permissions } = usePermissions();

    const resource = "serviceowner_ai_processors";
    const resourcePack = "serviceowner_pack";
    const title = "AI Processor"

    // Constant stage values
    const stages = ['config', 'verify', 'execution', 'change_instance_validator'];

    const triggerAIProcessor = (record: any) => {
        dataProvider.createAction(resourcePack, { data: undefined, verb: `trigger/service/${record?.service?.id}/${record.action_type}/` })
            .then((response: any) => notify('AI Processor is triggered'))
            .catch((error: any) => notify(JSON.stringify(error.body), 'error'))
    }

    const enableAIProcessor = (record: any, checked: boolean) => {
        dataProvider.update(resource, {
            id: record.id,
            data: { active: checked },
            previousData: { id: record.id },
        })
            .then(data => {
                notify(title + ' ' + (checked? "Enabled": "Disabled"));
            })
            .catch((error: any) => {
                if (error.status == 400)
                    notify('Error updating ' + title, 'error')
            })
    }

    const columns = [
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            render: (text: any, record: any) => (<>
                <div style={{ display: 'grid', placeItems: 'stretch' }}>
                    <Link style={{ whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }} to={`/${resource}/${record.id}`}>
                        {text}
                    </Link>
                </div>

            </>)
        },
        {
            title: 'LLM Model',
            dataIndex: ['llm_model', 'name'],
            key: 'llm_model',
        },
        {
            title: 'Service',
            dataIndex: ['service', 'name'],
            key: 'service',
        },
        {
            title: 'Type',
            dataIndex: 'action_type',
            key: 'action_type',
            render: (text: string) => text ? text.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : '',
        },
        {
            render: (text: any, record: any) =>
                <div>
                    {
                        permissions === "ADMIN" &&
                        <Switch
                            checkedChildren="Enabled"
                            unCheckedChildren="Disabled"
                            defaultChecked={record.active}
                            onChange={(checked) => enableAIProcessor(record, checked)}
                        />
                    }
                    {
                        permissions === "ADMIN" && ["optimiser", "change_instance_validator"].includes(record.action_type) &&
                        <Button size={"small"} style={{ marginLeft: '5px'}} disabled={!record.active} onClick={() => triggerAIProcessor(record)}>Trigger</Button>
                    }
                </div>

        },

    ]
    const tableData: Record[] = []
    ids.forEach(id => {
        tableData.push(data[id])
    });

    return (
        <Card
            bordered={false}
            className="criclebox tablespace mb-24 pack-card"
        >
            {/* Filters Row */}
            <div style={{ display: 'flex', gap: '16px', padding: '8px 16px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                <BaseFilterDropdown
                    // title="Service"
                    allTitle="All Services"
                    placeholder="Select service"
                    value={filterValues?.service_id}
                    onChange={(value) => setFilters({ ...filterValues, service_id: value }, {})}
                    resource="services"
                />
                <BaseFilterDropdown
                    // title="LLM Model"
                    allTitle="All Models"
                    placeholder="Select LLM model"
                    value={filterValues?.llm_model_id}
                    onChange={(value) => setFilters({ ...filterValues, llm_model_id: value }, {})}
                    resource="llm_models"
                />
                <div style={{ flex: '0 0 auto', minWidth: '200px' }}>
                    {/*<label className="filter-label">*/}
                    {/*    Stage*/}
                    {/*</label>*/}
                    <Select
                        placeholder="All Stages"
                        style={{ width: "250px" }}
                        className="filter-select"
                        allowClear
                        value={filterValues?.action_type}
                        onChange={(value) => setFilters({ ...filterValues, action_type: value }, {})}
                    >
                        <Option value={undefined}>All Stages</Option>
                        {stages.map((stage: string) => (
                            <Option key={stage} value={stage}>
                                {stage}
                            </Option>
                        ))}
                    </Select>
                </div>

                {permissions === "ADMIN" && (
                    <div style={{ marginLeft: 'auto' }}>
                        <Link to={`/${resource}/create`}>
                            <Button type='primary'>Create {title}</Button>
                        </Link>
                    </div>
                )}
            </div>

            <LiveTableWrapper refetch={refetch}>
                <SkeletonTable loading={loading} columns={columns as SkeletonTableColumnsType[]}>
                    <Table
                        rowKey='id'
                        columns={columns}
                        pagination={{ total: total, pageSize: perPage, current: page, onChange: page => setPage(page), className: "pagination-padding" }}
                        dataSource={tableData}
                    />
                </SkeletonTable>
            </LiveTableWrapper>
        </Card>
    )
}

const AIProcessorList = (props: ListProps) => (
    <>
        <PackDashboardTabs />
        <ListBase {...props}>
            <AIProcessorListView />
        </ListBase>
    </>
)


export default AIProcessorList;